import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../components/api/ApiClient';

const GOODS_SERVICES_API = '/internal-network/api/goods-services/';
const ADD_PRODUCT_API = '/user-preferences/api/user-product/';

const CATEGORY_OPTIONS = [
    'همه دسته‌ها',
    'شناسه اختصاصی',
    'شناسه اختصاصی تولید داخل',
    'شناسه اختصاصی وارداتی',
    'شناسه عمومی',
    'شناسه عمومی تولید داخل',
    'شناسه عمومی وارداتی'
];

function AddProductModal({ isVisible, onClose, onProductAdded, currentProducts }) {
    const [allGoods, setAllGoods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedType, setSelectedType] = useState('');

    const existingIds = useMemo(() => {
        return new Set(currentProducts.map(p => p.goods_and_services.id));
    }, [currentProducts]);

    const fetchAllGoods = async (type, category, search = '') => {
        setLoading(true);
        setError(null);
        try {
            let url = GOODS_SERVICES_API;
            const queryParams = ['limit=30'];

            if (search && search.trim()) {
                queryParams.push(`q=${encodeURIComponent(search.trim())}`);
            }

            if (category && category !== 'همه دسته‌ها') {
                queryParams.push(`category=${encodeURIComponent(category)}`);
            } else {
                queryParams.push('category=');
            }

            if (type === 'goods') {
                queryParams.push('service=false');
            } else if (type === 'service') {
                queryParams.push('service=true');
            }

            url += `?${queryParams.join('&')}`;

            const data = await apiClient(url, { method: 'GET' });
            setAllGoods(data || []);
        } catch (err) {
            console.error("خطا در دریافت لیست کالاها برای جستجو:", err);
            setError("خطا در بارگذاری لیست کالاها/خدمات برای جستجو.");
            setAllGoods([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isVisible) {
            fetchAllGoods(selectedType, selectedCategory, searchTerm);
        } else {
            // Reset search term when modal closes
            setSearchTerm('');
        }
    }, [isVisible, selectedType, selectedCategory]);

    // Debounce search term for API calls
    useEffect(() => {
        if (!isVisible) return;
        
        const timeoutId = setTimeout(() => {
            fetchAllGoods(selectedType, selectedCategory, searchTerm);
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const filteredGoods = useMemo(() => {
        if (!searchTerm || !searchTerm.trim()) return allGoods;
        const term = searchTerm.trim();
        return allGoods.filter(item =>
            (item.name && item.name.includes(term)) ||
            (item.identity && String(item.identity).includes(term))
        );
    }, [allGoods, searchTerm]);


    const handleAddProduct = async (goodsId) => {
        try {
            const payload = { iden: goodsId };
            const response = await apiClient(ADD_PRODUCT_API, {
                method: 'POST',
                data: payload,
                headers: { 'Content-Type': 'application/json' }
            });

            if (response === '') {
                alert("کالا/خدمت با موفقیت اضافه شد.");
                onProductAdded();
            } else {
                alert(`خطا در افزودن: ${response}`);
            }
        } catch (err) {
            console.error("خطا در افزودن محصول:", err);
            alert("خطا در افزودن کالا/خدمت.");
        }
    };


    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">جستجو و افزودن کالا/خدمت</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3 mb-4">
                            <div className="col-md-5">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="جستجو بر اساس نام یا شناسه (در نتایج فعلی)..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <select
                                    className="form-select"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    {CATEGORY_OPTIONS.map(cat => (
                                        <option key={cat} value={cat === 'همه دسته‌ها' ? '' : cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select"
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                >
                                    <option value="">همه نوع</option>
                                    <option value="goods">کالا</option>
                                    <option value="service">خدمت</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {loading ? (
                                <div className="text-center py-5">در حال بارگذاری اطلاعات...</div>
                            ) : error ? (
                                <div className="alert alert-danger">{error}</div>
                            ) : filteredGoods.length === 0 ? (
                                <div className="alert alert-info text-center">
                                    کالا/خدمت مطابق با فیلترهای اعمال شده یا جستجوی شما یافت نشد.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table
                                        className="table table-striped table-hover small"
                                        style={{ tableLayout: 'fixed', width: '100%' }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40%' }}>نام کالا/خدمت</th>
                                                <th style={{ width: '10%' }}>شناسه</th>
                                                <th style={{ width: '15%' }}>دسته بندی</th>
                                                <th style={{ width: '5%' }}>نوع</th>
                                                <th style={{ width: '5%' }}>مالیات</th>
                                                <th style={{ width: '10%' }}>عملیات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredGoods.map((item) => {
                                                const isAdded = existingIds.has(item.id);
                                                return (
                                                    <tr key={item.id}>
                                                        <td className="text-wrap">{item.name}</td>
                                                        <td>{item.identity}</td>
                                                        <td className="text-wrap">{item.category}</td>
                                                        <td>{item.service ? 'خدمت' : 'کالا'}</td>
                                                        <td>{item.taxPercent}%</td>
                                                        <td>
                                                            <button
                                                                className={`btn btn-sm ${isAdded ? 'btn-secondary' : 'btn-success'}`}
                                                                onClick={() => handleAddProduct(item.id)}
                                                                disabled={isAdded}
                                                                style={{ whiteSpace: 'nowrap' }}
                                                            >
                                                                {isAdded ? 'اضافه شده' : 'اضافه کردن'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>بستن</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddProductModal;